import { writable, get } from 'svelte/store'

export interface SystemPromptState {
  prompt: string
  isVisible: boolean
  lastUpdated: number | null
}

const STORAGE_KEY = 'korean-grammar-system-prompt'
const MAX_LENGTH = 500 // 글자 수 제한

// Load from localStorage
function loadSystemPrompt(): SystemPromptState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      // 빈 문자열이나 공백만 있는 경우 빈 상태로 처리
      const prompt = parsed.prompt && parsed.prompt.trim() ? parsed.prompt : ''
      return {
        prompt: prompt,
        isVisible: false,
        lastUpdated: prompt ? parsed.lastUpdated || null : null
      }
    }
  } catch (e) {
    console.error('Failed to load system prompt:', e)
  }
  
  return {
    prompt: '',
    isVisible: false,
    lastUpdated: null
  }
}

// Save to localStorage
function saveSystemPrompt(state: SystemPromptState) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      prompt: state.prompt,
      lastUpdated: state.lastUpdated
    }))
  } catch (e) {
    console.error('Failed to save system prompt:', e)
  }
}

// Create store
const { subscribe, update, set } = writable<SystemPromptState>(loadSystemPrompt())

// Subscribe to changes and save to localStorage
subscribe((state) => {
  if (state.prompt && state.prompt.trim() !== '') {
    saveSystemPrompt(state)
  } else {
    // 빈 프롬프트인 경우 localStorage에서 제거
    localStorage.removeItem(STORAGE_KEY)
  }
})

export const systemPromptStore = {
  subscribe,
  
  setPrompt(prompt: string) {
    // Enforce max length
    const trimmedPrompt = prompt.slice(0, MAX_LENGTH)
    
    update(state => ({
      ...state,
      prompt: trimmedPrompt,
      lastUpdated: Date.now()
    }))
  },
  
  toggleVisibility() {
    update(state => ({
      ...state,
      isVisible: !state.isVisible
    }))
  },
  
  show() {
    update(state => ({
      ...state,
      isVisible: true
    }))
  },
  
  hide() {
    update(state => ({
      ...state,
      isVisible: false
    }))
  },
  
  clear() {
    update(state => ({
      ...state,
      prompt: '',
      lastUpdated: null
    }))
    localStorage.removeItem(STORAGE_KEY)
  },
  
  getPrompt(): string {
    return get({ subscribe }).prompt
  },
  
  hasPrompt(): boolean {
    return get({ subscribe }).prompt.trim().length > 0
  },
  
  getMaxLength(): number {
    return MAX_LENGTH
  }
}